<template>
  <div
    class="App"
    :data-user-id="userId"
    :data-company-id="companyId"
    :data-user-admin="administrator"
    :class="{
      'in-owner-company': inOwnerCompany,
      'is-admin': administrator,
      'is-account': isAccount,
      'is-collaborator': isCollaborator,
      'is-contact': isContact,
      'alert-banner--is-active': isAlertBannerActive,
      'filter-sidebar-visible': !isFiltersCollapsed,
      'content-with-sidenav': shouldUseAppShell,
      'has-sidebar': hasSidebar,
      'has-open-quickview': hasOpenQuickView,
      'sidebar-closed': isSidebarCollapsed,
      'is-chat-conversation-visible': isEmbeddedChatConversationVisible,
      'is-chat-conversations-visible': isEmbeddedChatConversationListVisible,
    }"
  >
    <component :is="'style'" v-html="themeCSS" />

    <RouterView v-if="isHybridReady" />
    <HybridPopoverContainer />

    <!-- legacy TKO components -->
    <template v-if="isHybridReady">
      <TkoComponent name="hybrid-page-extras" />
      <TkoComponent name="section-dashboard-widgets" />
      <TkoComponent name="section-flash-alerts" />
      <TkoComponent name="widget-timers-list" />

      <OnboardingAndProductTours />
    </template>

    <div id="ModalContainer">
      <HybridModals />
    </div>
    <HybridQuickViews />

    <ProfilePopoverSingleton />
    <WindowEvents />
    <MountingPortal mount-to="body" append>
      <portal-target name="body" multiple />
    </MountingPortal>

    <PubbleChat
      v-if="showPubbleChat"
      ref="pubbleChat"
      :remove-transition="false"
      :current-user-name="fullName"
      :current-user-email="email"
    />
  </div>
</template>
<script>
import { useCurrentUser, useBranding, useCurrentAccount } from '@teamwork/use';
import { computed, watch, onMounted } from 'vue-demi';
import { MountingPortal } from 'portal-vue';
import { useLocalStorage } from '@vueuse/core';
import TkoComponent from '@sections/TkoComponent';
import HybridModals from '@sections/HybridModals';
import HybridQuickViews from '@sections/HybridQuickViews';
import WindowEvents from '@widgets/WindowEvents';
import HybridPopoverContainer from '@sections/HybridPopoverContainer';
import OnboardingAndProductTours from '@sections/OnboardingAndProductTours';
import { provideFilters } from '@sections/GenericFilter/composables/useFilters';

import buildThemeCSS from '@/utils/helpers/themeCSS';
import useStore from '@/platform/composables/useStore';
import { useHybrid } from '@/platform/composables/useHybrid';

import useRecencyProcessor from '@/platform/composables/useRecencyProcessor';
import { useAppShell } from '@/platform/composables/useAppShell';
import {
  inboxBaseFilterSymbol,
  provideInboxFilter,
} from '@/views/inbox/composables/useInboxFilter';
import { provideInboxUnreadCount } from '@/views/inbox/composables/useInboxUnreadCount';

import PubbleChat from '@/components/widgets/PubbleChat/PubbleChat.vue';
import { useFeatures } from '@/platform/composables/useFeatures';
import useExperiment from '@/platform/composables/useExperiment';
import { useLightspeedBridge } from '@/platform/composables/useLightspeedBridge';

// @vue/component
export default {
  name: 'App',
  components: {
    WindowEvents,
    TkoComponent,
    HybridModals,
    HybridQuickViews,
    HybridPopoverContainer,
    OnboardingAndProductTours,
    MountingPortal,
    PubbleChat,
    ProfilePopoverSingleton: () =>
      import(
        /* webpackChunkName: "profile-popover-singleton" */ '@sections/ProfilePopoverSingleton'
      ),
  },
  setup() {
    const user = useCurrentUser();
    const branding = useBranding();
    const store = useStore();
    const { isHybridReady } = useHybrid();
    useRecencyProcessor();

    const { shouldUseAppShell } = useAppShell();

    const {
      pubbleChatEnabled,
      budgetRetainersEnabled,
      marchPricingExpEnabled,
    } = useFeatures();
    const account = useCurrentAccount();

    // Set branding details in localStorage.
    useLocalStorage('teamwork/branding', branding.value);

    // we must initialise inbox filter at root of app because it
    // is shared by the inbox unread count and the inbox ui
    const inboxFilter = provideInboxFilter();
    provideFilters(
      {
        filter: inboxFilter,
        hasSavedFilters: false,
      },
      inboxBaseFilterSymbol,
    );

    const { fetch: fetchInboxUnreadCount } = provideInboxUnreadCount();
    fetchInboxUnreadCount();

    onMounted(() => {
      const { trackExperimentInPendo, trackPendoEvent } = useExperiment();
      trackExperimentInPendo('projects-pubble-chat-enabled', false);
      trackExperimentInPendo(
        'platform-checkout-exp-march-discount',
        false,
        true,
      );
      if (marchPricingExpEnabled.value) {
        trackPendoEvent('March_Discount_Enabled');
      }
      if (budgetRetainersEnabled.value) {
        trackPendoEvent('Retainer_Feature_Enabled');
      }
    });

    watch(
      store.state.user,
      (userStore) => {
        if (userStore.status === 'unauthenticated') {
          setTimeout(() => {
            console.info('App.vue watcher - redirecting to login');
            window.top.location.href = `/launchpad/login/projects?continue=${encodeURIComponent(
              window.top.location.href,
            )}`;
          }, 500);
        } else if (userStore.status === 'logged-out') {
          window.top.location.href = '/launchpad/logout/projects';
        }
      },
      { immediate: true },
    );

    const { isInLightspeed } = useLightspeedBridge();
    const startedFiveMonthsAgo = computed(() => {
      const today = new Date();
      const startingDate = new Date(account.value.dateSignedUp.utc().format());
      const fiveMonthsAgo = new Date(today.setMonth(today.getMonth() - 5));
      if (fiveMonthsAgo < startingDate) {
        return true;
      }
      return false;
    });

    return {
      isHybridReady,
      userId: computed(() => user.value.id),
      fullName: computed(
        () => `${user.value.firstName} ${user.value.lastName}`,
      ),
      email: computed(() => user.value.emailAddress),
      isPaid: computed(() => account.value.isPaid),
      administrator: computed(() => user.value.administrator),
      companyId: computed(() => user.value.companyId),
      userType: computed(() => user.value.userType),
      inOwnerCompany: computed(() => user.value.inOwnerCompany),
      isAccount: computed(() => user.value.userType === 'account'),
      isContact: computed(() => user.value.userType === 'contact'),
      isCollaborator: computed(() => user.value.userType === 'collaborator'),
      themeCSS: computed(() => buildThemeCSS(branding.value.theme.color)),
      // These need dependency from Vuex store removed
      isEmbeddedChatConversationVisible: computed(
        () => store.state.layout.isEmbeddedChatConversationVisible,
      ),
      isEmbeddedChatConversationListVisible: computed(
        () => store.state.layout.isEmbeddedChatConversationListVisible,
      ),
      isFiltersCollapsed: computed(() => store.state.layout.isFiltersCollapsed),
      hasOpenQuickView: computed(
        () => store.state.layout.hasOpenQuickView.hasOpenQuickView,
      ),
      isAlertBannerActive: computed(
        () => store.getters['layout/isAlertBannerActive'],
      ),
      isSidebarCollapsed: computed(
        () => store.getters['layout/isSidebarCollapsed'],
      ),
      shouldUseAppShell,
      hasSidebar: computed(() => store.getters['layout/hasSidebar']),
      triggerLiveChat() {
        this.$refs?.pubbleChat?.triggerLiveChat();
      },
      showPubbleChat: computed(
        () =>
          !isInLightspeed.value &&
          pubbleChatEnabled.value &&
          user.value.firstName &&
          user.value.lastName &&
          user.value.emailAddress &&
          startedFiveMonthsAgo.value &&
          account.value.isPaid,
      ),
    };
  },
};
</script>
